/**
 * IFC model repository — fetches IFC_PROCESSED documents, queries the
 * relational data.ifc_* tables via Postgres RPCs, and signs the glTF blob
 * for the /model page.
 *
 * The 3D viewer prefers real glTF geometry (produced by IfcConvert during
 * pipeline processing) when cloud_metadata.gltf_path is set. When it isn't
 * (legacy docs or rare conversion failures) the viewer falls back to a
 * procedural scene built from the spatial-tree RPC.
 *
 * RLS on public.documents enforces the org boundary for document listing.
 * The RPCs are SECURITY INVOKER with an EXISTS-on-documents guard, so the
 * data.ifc_* tables (which themselves have no RLS, matching the rest of the
 * data.* schema) can't leak across orgs.
 */

import { supabase } from "@/integrations/supabase/client";

/** Subset of public.documents needed by the viewer. */
export interface IfcModelRow {
  id: number;
  filename: string;
  state: string;
  file_path: string;
  size: number;
  created_at: string;
  /** project this IFC document belongs to — used to default the project when
   * creating a PM issue from an element (WHA-3521). */
  project_id: number | null;
  /** folder this IFC document lives in — drives the picker's folder-path hover
   * tooltip (WHA-3708). Null for documents not filed under a folder. */
  folder_id: number | null;
  /** Version number within its chain (1-based). Drives the picker's `v{n}`
   * badges and version grouping (WHA-3712). */
  version: number | null;
  /** Shared id linking every version of the same logical file. Null for files
   * that were never re-versioned (WHA-3712). */
  version_group: string | null;
  /** Whether this is the head of its version chain. The picker only offers
   * latest versions; a superseded revision is loaded only when a PM deep-link
   * targets that exact file (WHA-3688). */
  is_latest_version: boolean;
  cloud_metadata: Record<string, unknown> | null;
}

/** One spatial-tree node as returned by the get_ifc_spatial_tree RPC. */
export interface IfcSpatialNode {
  element_guid: string;
  ifc_class: string;
  name: string | null;
  long_name: string | null;
  parent_guid: string | null;
  storey_guid: string | null;
  elevation: number | null;
  element_class_counts: Record<string, number> | null;
  area: number | null;
  volume: number | null;
  height: number | null;
}

/** Shape returned by the get_ifc_metrics RPC — matches legacy metrics_json. */
export interface IfcMetrics {
  kpis: {
    storey_count: number;
    space_count: number;
    element_count: number;
    gross_area_sqm: number | null;
    volume_m3: number | null;
    material_count: number;
  };
  elements_by_class: { ifc_class: string; count: number }[];
  top_materials: { material: string; uses: number }[];
  elements_per_storey: {
    storey_name: string | null;
    elevation: number | null;
    counts_by_class: Record<string, number>;
  }[];
  storey_elevations: {
    name: string | null;
    elevation: number | null;
    height: number | null;
  }[];
  space_area_histogram: { bin_edges: number[]; counts: number[] };
}

export interface IfcElementProperty {
  source: "pset" | "qto";
  pset_name: string;
  property_name: string;
  value_text: string | null;
  value_number: number | null;
  value_bool: boolean | null;
  value_json: unknown;
}

export interface IfcElementMaterialAssignment {
  name: string;
  description: string | null;
  relation_kind: "single" | "list" | "layer_set" | "constituent_set";
  layer_index: number | null;
  layer_thickness: number | null;
}

export interface IfcElementDetails {
  element: {
    id: number;
    guid: string;
    type: string;
    name: string | null;
    long_name: string | null;
    parent_guid: string | null;
    storey_guid: string | null;
    elevation: number | null;
    /** Stable cross-revision identity (data.ifc_element_anchors.id) for PM
     * linkage; null only if the pipeline hasn't anchored the element. */
    anchor_id: number | null;
  };
  properties: IfcElementProperty[];
  materials: IfcElementMaterialAssignment[];
}

/** How a single element differs between the two compared documents. */
export type IfcChangeType = "added" | "removed" | "modified";

/** One changed element row from get_ifc_model_diff (added/removed/modified
 * only — unchanged elements are excluded from the payload). */
export interface IfcElementChange {
  guid: string;
  ifc_class: string;
  name: string | null;
  storey_guid: string | null;
  change_type: IfcChangeType;
  /** For modified rows: which aspect(s) differ. All false for added/removed. */
  prop_changed: boolean;
  material_changed: boolean;
  attr_changed: boolean;
  /** Authoritative "can this change be drawn in 3D" — derived server-side from
   * data.ifc_elements.has_geometry (added→target, removed→base, modified→either).
   * Optional: pre-renderable-RPC payloads omit it; consumers fall back then. */
  renderable?: boolean;
}

/** A KPI's before/after/delta across the two documents (delta null when either
 * side is unmeasured). */
export interface IfcDiffMetricDelta {
  before: number | null;
  after: number | null;
  delta: number | null;
}

/** Storey name/elevation lookup (union of both documents) for grouping changes
 * by storey — unchanged storeys are absent from `changes`, so this is the only
 * source of their names (WHA-3695 UI follow-up). */
export interface IfcStoreyRef {
  guid: string;
  name: string | null;
  elevation: number | null;
}

/** Shape returned by the get_ifc_model_diff RPC (WHA-3695). A = base/older,
 * B = target/newer. */
export interface IfcModelDiff {
  base_doc_id: number;
  target_doc_id: number;
  summary: {
    added: number;
    removed: number;
    modified: number;
    unchanged: number;
    /** Per-bucket count of changes that can actually be drawn in 3D (WHA).
     * Optional: absent on pre-renderable-RPC payloads. */
    renderable?: { added: number; removed: number; modified: number };
  };
  changes: IfcElementChange[];
  storeys: IfcStoreyRef[];
  metric_deltas: {
    storey_count: IfcDiffMetricDelta;
    space_count: IfcDiffMetricDelta;
    element_count: IfcDiffMetricDelta;
    material_count: IfcDiffMetricDelta;
    gross_area_sqm: IfcDiffMetricDelta;
    volume_m3: IfcDiffMetricDelta;
  };
}

/** A signed URL plus its expiry timestamp (epoch ms). */
export interface SignedAsset {
  url: string;
  expiresAt: number;
}

/** Signed URLs valid for ~1h — plenty of headroom for a viewing session. */
const SIGNED_URL_TTL_SECONDS = 60 * 60;

// ---------------------------------------------------------------------------
// Document list + glTF signing
// ---------------------------------------------------------------------------

const IFC_MODEL_COLUMNS =
  "id, filename, state, file_path, size, created_at, project_id, folder_id, is_latest_version, version, version_group, cloud_metadata";

// Security note: this filters by building only (no `project_id` filter) and
// leans entirely on the `documents_select` RLS policy for per-user scoping. For
// a subcontractor that policy returns only rows whose project they're assigned
// to, so the 3D viewer's model list is already limited to their projects' .ifc
// files. Keep it that way — do NOT grant subcontractors VIEW_ASSIGNED_BUILDINGS
// (it would widen them to every IFC in the building, regardless of project).
export async function fetchIfcModelsForBuilding(
  buildingId: number,
  orgId: string
): Promise<IfcModelRow[]> {
  const { data, error } = await supabase
    .from("documents")
    .select(IFC_MODEL_COLUMNS)
    .eq("org_id", orgId)
    .eq("building_id", buildingId)
    .eq("container_type", "ifc")
    .eq("state", "IFC_PROCESSED")
    .eq("is_deleted", false)
    // Every processed version (not just `is_latest_version`): the picker groups
    // older versions under the latest one and lets the user expand + load them
    // (WHA-3712). `latestModels` in the store derives the top-level list.
    .order("created_at", { ascending: false });

  if (error) {
    throw new Error(`Failed to fetch IFC models: ${error.message}`);
  }
  return (data ?? []) as IfcModelRow[];
}

/**
 * Fetch one IFC document by id, **regardless of version-chain position**, so a
 * PM "View in 3D" deep-link (`?ifc=<doc>`) still loads the exact file it was
 * authored against even after a newer revision supersedes it (WHA-3688). The
 * picker uses `fetchIfcModelsForBuilding` (latest-only); this is the escape
 * hatch the store calls when a requested doc isn't in that list.
 *
 * Returns null if the doc doesn't exist, isn't a processed IFC, is soft-
 * deleted, or RLS hides it — the caller simply skips loading it.
 */
export async function fetchIfcModelById(
  documentId: number,
  orgId: string
): Promise<IfcModelRow | null> {
  const { data, error } = await supabase
    .from("documents")
    .select(IFC_MODEL_COLUMNS)
    .eq("id", documentId)
    .eq("org_id", orgId)
    .eq("container_type", "ifc")
    .eq("state", "IFC_PROCESSED")
    .eq("is_deleted", false)
    .maybeSingle();

  if (error) {
    throw new Error(`Failed to fetch IFC model ${documentId}: ${error.message}`);
  }
  return (data ?? null) as IfcModelRow | null;
}

/**
 * Resolve the signed URL of an IFC document's preview thumbnail (WHA-3685).
 *
 * The pipeline renders one canonical PNG per IFC (iso angle, matching the
 * /models viewer) and records its Storage path in the public.document_thumbnails
 * table. The .ifc preview modal — opened from chatbot chips, the building-data
 * sidebar, and the /documents + /projects lists — fetches it by document id
 * alone, so every surface shows the exact same image without per-call-site
 * plumbing. RLS on document_thumbnails inherits the document's org scoping.
 *
 * Returns null (modal falls back to its non-image state) when the document has
 * no thumbnail yet (old/not-yet-reprocessed IFC), is hidden by RLS, or signing
 * fails. Never throws — a preview must not error a list/chat view.
 */
export async function getIfcThumbnailUrl(documentId: number): Promise<string | null> {
  const { data, error } = await supabase
    .from("document_thumbnails")
    .select("path")
    .eq("document_id", documentId)
    .maybeSingle();
  if (error || !data?.path) return null;

  const { data: signed, error: signError } = await supabase.storage
    .from("documents")
    .createSignedUrl(data.path, SIGNED_URL_TTL_SECONDS);
  if (signError || !signed?.signedUrl) return null;
  return signed.signedUrl;
}

export async function fetchSignedGltfUrl(filePath: string): Promise<SignedAsset> {
  const { data, error } = await supabase.storage
    .from("documents")
    .createSignedUrl(filePath, SIGNED_URL_TTL_SECONDS);
  if (error || !data?.signedUrl) {
    throw new Error(`Failed to sign glTF URL for ${filePath}: ${error?.message ?? "no signedUrl"}`);
  }
  return {
    url: data.signedUrl,
    expiresAt: Date.now() + SIGNED_URL_TTL_SECONDS * 1000,
  };
}

/**
 * Fetch a Storage object using a signed URL, transparently re-signing once
 * on 401/403 (expired or revoked). Lets viewer sessions survive past the
 * 1 h TTL without bookkeeping timers (which drift in backgrounded tabs).
 *
 * Designed as a thin replacement for `fetch(signedUrl)` call sites: pass
 * the file path instead of a pre-signed URL, and the wrapper signs +
 * retries internally. (WHA-3578)
 */
export async function fetchWithUrlRefresh(filePath: string, init?: RequestInit): Promise<Response> {
  const signed = await fetchSignedGltfUrl(filePath);
  const res = await fetch(signed.url, init);
  if (res.status === 401 || res.status === 403) {
    const fresh = await fetchSignedGltfUrl(filePath);
    return fetch(fresh.url, init);
  }
  return res;
}

// ---------------------------------------------------------------------------
// SQL-backed queries (Phase 1.5 — WHA-3557)
// ---------------------------------------------------------------------------

/**
 * Flat list of spatial nodes (project, site, building, storey, space) for one
 * IFC document. Frontend assembles the parent→children tree using parent_guid
 * (≤5 levels deep so the in-JS build is trivial).
 */
export async function fetchIfcSpatialTree(documentId: number): Promise<IfcSpatialNode[]> {
  const { data, error } = await supabase.rpc("get_ifc_spatial_tree", {
    p_ifc_document_id: documentId,
  });
  if (error) {
    throw new Error(`Failed to fetch IFC spatial tree: ${error.message}`);
  }
  return (data ?? []) as IfcSpatialNode[];
}

/**
 * WHA-3635: element GUIDs contained by a spatial container node. For a storey
 * → all elements with that storey_guid; for building/site/project → every
 * physical element; for a space → its directly-parented elements; for a
 * physical element → itself. Used to highlight a whole node's geometry when a
 * tree node (which has no mesh of its own) is picked.
 */
/** WHA-3689: session cache + in-flight de-dupe per (doc, node). A container's
 * member set is immutable for a given IFC document (re-uploads create a NEW
 * document id), so re-clicking the same tree node must not re-fetch. Failed
 * requests are evicted so a retry hits the network again. */
const memberGuidsCache = new Map<string, Promise<string[]>>();

export async function fetchIfcNodeMemberGuids(
  documentId: number,
  nodeGuid: string
): Promise<string[]> {
  const key = `${documentId}:${nodeGuid}`;
  const cached = memberGuidsCache.get(key);
  if (cached) return cached;

  const request = (async () => {
    const { data, error } = await supabase.rpc("get_ifc_node_member_guids", {
      p_ifc_document_id: documentId,
      p_node_guid: nodeGuid,
    });
    if (error) {
      throw new Error(`Failed to fetch IFC node member GUIDs: ${error.message}`);
    }
    return ((data ?? []) as { element_guid: string }[]).map((r) => r.element_guid);
  })();

  memberGuidsCache.set(key, request);
  try {
    return await request;
  } catch (e) {
    memberGuidsCache.delete(key); // don't cache failures
    throw e;
  }
}

/** One physical element leaf, for the spatial tree's per-storey drill-down. */
export interface IfcLeafElement {
  element_guid: string;
  ifc_class: string;
  name: string | null;
  long_name: string | null;
}

/**
 * Physical elements of a given IFC class contained by one storey — drives the
 * spatial tree's "Walls · 42" class-group drill-down. Keyed on
 * (document, storey_guid, ifc_class) so the rows align 1:1 with the storey's
 * `element_class_counts`. Lazily fetched per class group on expand.
 */
export async function fetchIfcNodeElements(
  documentId: number,
  storeyGuid: string,
  ifcClass: string
): Promise<IfcLeafElement[]> {
  const { data, error } = await supabase.rpc("get_ifc_node_elements", {
    p_ifc_document_id: documentId,
    p_storey_guid: storeyGuid,
    p_ifc_class: ifcClass,
  });
  if (error) {
    throw new Error(`Failed to fetch IFC node elements: ${error.message}`);
  }
  return (data ?? []) as IfcLeafElement[];
}

/** Session cache of document-wide class → element GUIDs (a class's membership
 * is fixed for a loaded model), keyed `${docId}:${ifcClass}`. Mirrors
 * `memberGuidsCache`. */
const elementsByClassCache = new Map<string, Promise<string[]>>();

/**
 * Every element GUID of one IFC class across a whole document — drives the
 * viewer's "highlight all similar elements" action. Document-wide (unlike
 * fetchIfcNodeElements, which is storey-scoped). Session-cached per
 * (document, class) since membership is fixed for a loaded model.
 */
export async function fetchIfcElementsByClass(
  documentId: number,
  ifcClass: string
): Promise<string[]> {
  const key = `${documentId}:${ifcClass}`;
  const cached = elementsByClassCache.get(key);
  if (cached) return cached;

  const request = (async () => {
    const { data, error } = await supabase.rpc("get_ifc_elements_by_class", {
      p_ifc_document_id: documentId,
      p_ifc_class: ifcClass,
    });
    if (error) {
      throw new Error(`Failed to fetch IFC elements by class: ${error.message}`);
    }
    return ((data ?? []) as { element_guid: string }[]).map((r) => r.element_guid);
  })();

  elementsByClassCache.set(key, request);
  try {
    return await request;
  } catch (e) {
    elementsByClassCache.delete(key); // don't cache failures
    throw e;
  }
}

/** Aggregated metrics matching the legacy metrics_json shape. */
export async function fetchIfcMetrics(documentId: number): Promise<IfcMetrics | null> {
  const { data, error } = await supabase.rpc("get_ifc_metrics", {
    p_ifc_document_id: documentId,
  });
  if (error) {
    throw new Error(`Failed to fetch IFC metrics: ${error.message}`);
  }
  return (data ?? null) as IfcMetrics | null;
}

/** Element header + every Pset/Qto row + every material association. */
/** WHA-3632: in-flight de-dupe. On a PM-enabled element click the viewer and
 * the property panel both call this in the same tick; share the one RPC. The
 * entry is cleared once settled so later clicks always fetch fresh. */
const inflightElementProps = new Map<string, Promise<IfcElementDetails | null>>();

export async function fetchIfcElementProperties(
  documentId: number,
  elementGuid: string
): Promise<IfcElementDetails | null> {
  const key = `${documentId}:${elementGuid}`;
  const existing = inflightElementProps.get(key);
  if (existing) return existing;

  const request = (async () => {
    const { data, error } = await supabase.rpc("get_ifc_element_properties", {
      p_ifc_document_id: documentId,
      p_element_guid: elementGuid,
    });
    if (error) {
      throw new Error(`Failed to fetch IFC element properties: ${error.message}`);
    }
    return (data ?? null) as IfcElementDetails | null;
  })();

  inflightElementProps.set(key, request);
  try {
    return await request;
  } finally {
    inflightElementProps.delete(key);
  }
}

/**
 * Structural diff between two processed IFC documents, keyed by IFC GlobalId
 * (WHA-3695). `baseDocId` is the older/base revision (A), `targetDocId` the
 * newer/target (B): added = in B only, removed = in A only, modified = same
 * GUID with a differing per-element fingerprint. Returns null if either
 * document is hidden by RLS (the SECURITY INVOKER RPC's EXISTS guard).
 *
 * Per-element property/material detail is NOT included here — the diff panel
 * lazily fetches both sides via `fetchIfcElementProperties` and diffs them in
 * the client when a modified element is selected.
 */
export async function fetchIfcModelDiff(
  baseDocId: number,
  targetDocId: number
): Promise<IfcModelDiff | null> {
  // The cross-model diff is a heavy analytical RPC (per-element property/material
  // fingerprints over hundreds of thousands of rows). It runs in ~1.5s warm, but
  // a cold cache or DB contention can intermittently blow the role's
  // statement_timeout. A per-function timeout can't override that (the timer is
  // armed at the top-level statement), so retry instead: the next attempt runs
  // against a now-warm cache and almost always succeeds (WHA-3695 cont.6).
  const MAX_ATTEMPTS = 3;
  let lastError: { code?: string; message?: string } | null = null;
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    const { data, error } = await supabase.rpc("get_ifc_model_diff", {
      p_doc_a: baseDocId,
      p_doc_b: targetDocId,
    });
    if (!error) return (data ?? null) as IfcModelDiff | null;
    lastError = error;
    // Retry only transient cancellations (statement timeout = SQLSTATE 57014);
    // a real error (bad args, RLS) should fail fast.
    const retriable =
      error.code === "57014" || /statement timeout|canceling statement/i.test(error.message ?? "");
    if (!retriable || attempt === MAX_ATTEMPTS) break;
    await new Promise((resolve) => setTimeout(resolve, 500 * attempt));
  }
  throw new Error(`Failed to fetch IFC model diff: ${lastError?.message ?? "unknown error"}`);
}
